import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import gph from "../../../assets/images/logo-georanking.png";

const topics = [
  { id: "apresentacao", title: "1. Apresentação" },
  { id: "definicoes", title: "2. Definições" },
  { id: "informacoes-gerais", title: "3. Informações Gerais" },
  { id: "principios", title: "4. Princípios Aplicáveis" },
  { id: "dados-coletados", title: "5. Dados Pessoais Coletados" },
  { id: "finalidades", title: "6. Finalidades do Tratamento" },
  { id: "bases-legais", title: "7. Bases Legais do Tratamento" },
  { id: "compartilhamento", title: "8. Compartilhamento de Dados" },
  { id: "armazenamento", title: "9. Armazenamento e Retenção" },
  { id: "seguranca", title: "10. Medidas de Segurança" },
  { id: "cookies", title: "11. Cookies e Tecnologias Similares" },
  { id: "direitos", title: "12. Direitos dos Titulares" },
  { id: "menores", title: "13. Dados de Menores de Idade" },
  { id: "alteracoes", title: "14. Alterações na Política" },
  { id: "legislacao", title: "15. Legislação e Foro" },
  { id: "disposicoes-finais", title: "16. Disposições Finais" },
];

const drawerWidth = 280;

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <Box id={id} sx={{ pt: 10, mt: -8, mb: 4 }}>
    <Typography
      variant="h5"
      component="h2"
      gutterBottom
      sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const Subtitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h6"
    component="h3"
    gutterBottom
    sx={{ mt: 2, fontWeight: 500 }}
  >
    {children}
  </Typography>
);

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState(topics[0].id);
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      let current = "";
      topics.forEach((topic) => {
        const element = document.getElementById(topic.id);
        if (element && element.getBoundingClientRect().top < 120) {
          current = topic.id;
        }
      });
      setActiveId(current);
    };

    const contentEl = mainContentRef.current;
    contentEl?.addEventListener("scroll", handleScroll);
    return () => contentEl?.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <Box>
      <Toolbar />
      <List>
        {topics.map((topic) => (
          <ListItem key={topic.id} disablePadding>
            <ListItemButton
              selected={activeId === topic.id}
              onClick={() => handleNavClick(topic.id)}
            >
              <ListItemText
                primary={topic.title}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: activeId === topic.id ? "bold" : "regular",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "#ffffff",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
        elevation={0}
      >
        <Toolbar>
          {isMobile ? (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{
                mr: 1,
                zIndex: 1001,
                position: "relative",
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <img src={gph} alt="Logo" style={{ height: 52, marginRight: 16 }} />
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Declaração de Privacidade
          </Typography>
          {!isMobile && (
            <IconButton
              color="inherit"
              edge="end"
              onClick={() => navigate("/login")}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "#f7f7f7",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        ref={mainContentRef}
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          overflowY: "auto",
          color: "black",
        }}
      >
        <Toolbar />
        <Container maxWidth="md">
          <Typography variant="caption" display="block" gutterBottom>
            Data de última atualização: 22/10/2025
          </Typography>

          <Section id="apresentacao" title="1. APRESENTAÇÃO">
            <Typography paragraph>
              A presente Política de Privacidade ("Política") visa informar de
              forma clara, precisa e transparente como a ADS MANAGER, realiza o
              tratamento de dados pessoais dos usuários do serviço de Software
              as a Service (SaaS) destinado à otimização e impulsionamento do
              ranqueamento de perfis comerciais no Google.
            </Typography>
            <Typography paragraph>
              Este documento é elaborado em conformidade com a Lei Geral de
              Proteção de Dados Pessoais (Lei nº 13.709/2018 - "LGPD"), o Marco
              Civil da Internet (Lei nº 12.965/2014) e demais normas aplicáveis
              à proteção de dados pessoais e privacidade.
            </Typography>
            <Typography paragraph>
              Ao utilizar nossos Serviços, o Cliente, ora CONTRATANTE declara
              ter lido, compreendido e concordado com os termos desta Política
              de Privacidade, bem como com o Termo de Uso da Plataforma.
            </Typography>
          </Section>

          <Section id="definicoes" title="2. DEFINIÇÕES">
            <Typography paragraph>
              Para os fins desta Política, são adotadas as seguintes definições,
              em conformidade com a LGPD:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Dados Pessoais: informação relacionada a pessoa natural identificada ou identificável, como nome, e-mail, telefone, dados de login e informações de perfil comercial."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Dados Pessoais Sensíveis: dados pessoais sobre origem racial ou étnica, convicção religiosa, opinião política, filiação a sindicato ou a organização de caráter religioso, filosófico ou político, dado referente à saúde ou à vida sexual, dado genético ou biométrico, quando vinculado a uma pessoa natural."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Titular: pessoa natural a quem se referem os dados pessoais que são objeto de tratamento."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Controlador: pessoa natural ou jurídica, de direito público ou privado, a quem competem as decisões referentes ao tratamento de dados pessoais. Neste contexto, a ADS MANAGER atua como Controladora."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Operador: pessoa natural ou jurídica, de direito público ou privado, que realiza o tratamento de dados pessoais em nome do Controlador."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Tratamento: toda operação realizada com dados pessoais, como coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação ou controle da informação, modificação, comunicação, transferência, difusão ou extração."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Encarregado (DPO): pessoa indicada pelo Controlador para atuar como canal de comunicação entre o Controlador, os titulares dos dados e a Autoridade Nacional de Proteção de Dados."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Consentimento: manifestação livre, informada e inequívoca pela qual o titular concorda com o tratamento de seus dados pessoais para uma finalidade determinada."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Anonimização: utilização de meios técnicos razoáveis e disponíveis no momento do tratamento, por meio dos quais um dado perde a possibilidade de associação, direta ou indireta, a um indivíduo."
                />
              </ListItem>
            </List>
          </Section>

          <Section
            id="informacoes-gerais"
            title="3. INFORMAÇÕES GERAIS SOBRE O TRATAMENTO DE DADOS"
          >
            <Subtitle>3.1. Agente de Tratamento</Subtitle>
            <Typography paragraph>
              A ADS MANAGER atua como Controladora dos dados pessoais coletados
              e tratados por meio da Plataforma, sendo responsável pelas
              decisões referentes ao tratamento, em conformidade com o artigo
              5º, VI, da LGPD.
            </Typography>
            <Subtitle>3.2. Encarregado de Proteção de Dados (DPO)</Subtitle>
            <Typography paragraph>
              Nos termos do artigo 41 da LGPD, a ADS MANAGER indica como
              Encarregado pelo Tratamento de Dados Pessoais (Data Protection
              Officer - DPO):
            </Typography>
            <Typography paragraph>
              <strong>(DPO):</strong> Valeska [SOBRENOME]
              <br />
              <strong>E-mail do DPO:</strong> privacidade@adssoftware.com.br
              (alias opcional: dpo@adssoftware.com.br)
              <br />
              <strong>Endereço:</strong> Avenida Monte Carlo, 231, Goiânia/GO,
              CEP 74370-405
              <br />
              <strong>Telefone:</strong> (62) [inserir]
            </Typography>
            <Typography paragraph>
              O Encarregado está disponível para esclarecer dúvidas, receber
              comunicações da ANPD e orientar colaboradores e Clientes sobre as
              práticas de proteção de dados adotadas.
            </Typography>
          </Section>

          <Section
            id="principios"
            title="4. PRINCÍPIOS APLICÁVEIS AO TRATAMENTO DE DADOS"
          >
            <Typography paragraph>
              O tratamento de dados pessoais pela ADS MANAGER observa
              rigorosamente os princípios estabelecidos no artigo 6º da LGPD:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Finalidade: realização do tratamento para propósitos legítimos, específicos, explícitos e informados ao titular, sem possibilidade de tratamento posterior incompatível com essas finalidades."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Adequação: compatibilidade do tratamento com as finalidades informadas ao titular, de acordo com o contexto do tratamento."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Necessidade: limitação do tratamento ao mínimo necessário para a realização de suas finalidades, com dados pertinentes, proporcionais e não excessivos."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Livre Acesso: garantia aos titulares de consulta facilitada e gratuita sobre a forma e a duração do tratamento, bem como sobre a integralidade de seus dados pessoais."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Qualidade dos Dados: garantia aos titulares de exatidão, clareza, relevância e atualização dos dados, de acordo com a necessidade e para o cumprimento da finalidade do tratamento."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Transparência: garantia aos titulares de informações claras, precisas e facilmente acessíveis sobre a realização do tratamento e os respectivos agentes de tratamento."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Segurança: utilização de medidas técnicas e administrativas aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou difusão."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Prevenção: adoção de medidas para prevenir a ocorrência de danos em virtude do tratamento de dados pessoais."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Não Discriminação: impossibilidade de realização do tratamento para fins discriminatórios ilícitos ou abusivos."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Responsabilização e Prestação de Contas: demonstração pelo agente da adoção de medidas eficazes e capazes de comprovar a observância e o cumprimento das normas de proteção de dados pessoais."
                />
              </ListItem>
            </List>
          </Section>

          <Section id="dados-coletados" title="5. DADOS PESSOAIS COLETADOS">
            <Subtitle>5.1. Categorias de Dados Coletados</Subtitle>
            <Typography paragraph>
              No contexto da prestação dos Serviços de marketing digital,
              gerenciamento de perfis comerciais e otimização de ranqueamento no
              Google, a ADS MANAGER coleta e trata as seguintes categorias de
              dados pessoais:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              5.1.1. Dados de Identificação e Cadastro
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Nome completo, Razão social ou nome empresarial, CNPJ ou CPF, Endereço comercial, Telefone de contato, Endereço de e-mail, Dados do representante legal da empresa"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              5.1.2. Dados de Acesso e Autenticação
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Dados de autenticação eletrônica, Registros de acesso (data, hora, endereço IP), Informações sobre o dispositivo utilizado, Dados de sessão de navegação"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              5.1.3. Dados Financeiros e de Pagamento
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Dados processados pela plataforma Stripe"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              5.1.4. Dados do Perfil Comercial
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Informações do perfil comercial no Google, Palavras-chave selecionadas (até 8 palavras-chave), Dados de performance e métricas de ranqueamento, Histórico de impulsionamentos realizados, Dados de monitoramento e automações"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              5.1.5. Dados de Comunicação
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Histórico de comunicações via e-mail, Registros de atendimento via WhatsApp, Registros de chamadas telefônicas, Mensagens enviadas através dos canais de suporte"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              5.1.6. Dados de Navegação e Cookies
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Endereço IP, Tipo de navegador utilizado, Páginas acessadas na Plataforma, Tempo de permanência, Origem do acesso, Cookies e tecnologias similares"
                />
              </ListItem>
            </List>
            <Subtitle>5.2. Dados que NÃO coletamos</Subtitle>
            <Typography paragraph>
              - Não armazenamos dados completos de cartão (como CVV) nos
              sistemas da ADS MANAGER. O processamento é feito pela Stripe, com
              tokenização e conformidade PCI-DSS; a ADS MANAGER recebe tokens e
              metadados para cobrança/conciliação.
            </Typography>
            <Typography paragraph>
              A ADS MANAGER não coleta dados pessoais sensíveis, nos termos do
              artigo 5º, II, da LGPD, tais como origem racial ou étnica,
              convicções religiosas, opiniões políticas, filiação sindical,
              dados de saúde, vida sexual, dados genéticos ou biométricos.
            </Typography>
          </Section>

          <Section
            id="finalidades"
            title="6. FINALIDADES DO TRATAMENTO DE DADOS"
          >
            <Typography paragraph>
              Os dados pessoais coletados são tratados pela ADS MANAGER
              exclusivamente para as seguintes finalidades específicas,
              explícitas e legítimas:
            </Typography>
            <Subtitle>6.1. Prestação dos Serviços Contratados</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Administração e gerenciamento de perfis comerciais no Google, Otimização e impulsionamento do ranqueamento de perfis, Monitoramento de performance e métricas, Implementação de automações correlatas, Inserção e gerenciamento de palavras-chave"
                />
              </ListItem>
            </List>
            <Subtitle>6.2. Gestão Contratual e Comercial</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Identificação e autenticação do Cliente, Execução e cumprimento do Termo de Uso, Gestão da relação contratual, Processamento de pagamentos e cobranças, Emissão de notas fiscais e documentos fiscais"
                />
              </ListItem>
            </List>
            <Subtitle>6.3. Comunicação com o Cliente</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Envio de notificações sobre os Serviços, Resposta a solicitações e atendimento ao suporte, Comunicação sobre alterações contratuais, Envio de informações sobre reajustes de valores, Notificações sobre período de cortesia e cobranças"
                />
              </ListItem>
            </List>
            <Subtitle>6.4. Cumprimento de Obrigações Legais</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Atendimento a determinações judiciais e administrativas, Cumprimento de obrigações fiscais e tributárias, Atendimento a requisições de autoridades competentes, Cumprimento de obrigações contábeis e regulatórias"
                />
              </ListItem>
            </List>
            <Subtitle>6.5. Segurança e Prevenção de Fraudes</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Prevenção e detecção de fraudes e uso indevido da Plataforma, Proteção contra acessos não autorizados, Monitoramento de segurança da informação, Identificação de atividades suspeitas ou ilícitas"
                />
              </ListItem>
            </List>
            <Subtitle>6.6. Melhoria dos Serviços</Subtitle>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Análise de desempenho da Plataforma, Identificação de melhorias e correção de falhas, Desenvolvimento de novas funcionalidades, Otimização da experiência do usuário"
                />
              </ListItem>
            </List>
          </Section>

          <Section id="bases-legais" title="7. BASES LEGAIS DO TRATAMENTO">
            <Typography paragraph>
              Em conformidade com o artigo 7º da LGPD, o tratamento de dados
              pessoais pela ADS MANAGER fundamenta-se nas seguintes bases
              legais:
            </Typography>
            <Subtitle>7.1. Consentimento (Art. 7º, I, LGPD)</Subtitle>
            <Typography paragraph>
              O tratamento de determinados dados pessoais é realizado mediante o
              fornecimento de consentimento livre, informado e inequívoco do
              Titular, especialmente para:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Envio de comunicações de marketing e promocionais, Tratamento de dados para finalidades não essenciais aos Serviços, Utilização de cookies não essenciais"
                />
              </ListItem>
            </List>
            <Subtitle>7.2. Execução de Contrato (Art. 7º, V, LGPD)</Subtitle>
            <Typography paragraph>
              O tratamento é necessário para a execução do Termo de Uso e dos
              Serviços contratados:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Criação e gestão da conta do Cliente, Prestação dos Serviços de otimização de ranqueamento, Processamento de pagamentos, Gerenciamento de perfis comerciais"
                />
              </ListItem>
            </List>
            <Subtitle>
              7.3. Cumprimento de Obrigação Legal (Art. 7º, II, LGPD)
            </Subtitle>
            <Typography paragraph>
              O tratamento é necessário para o cumprimento de obrigação legal ou
              regulatória pela ADS MANAGER:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Emissão de notas fiscais, Cumprimento de obrigações tributárias, Atendimento a requisições de autoridades competentes, Manutenção de registros contábeis"
                />
              </ListItem>
            </List>
            <Subtitle>
              7.4. Exercício Regular de Direitos (Art. 7º, VI, LGPD)
            </Subtitle>
            <Typography paragraph>
              O tratamento é realizado para o exercício regular de direitos em
              processos judiciais, administrativos ou arbitrais:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Defesa em processos judiciais, Constituição de provas, Proteção de direitos contratuais"
                />
              </ListItem>
            </List>
            <Subtitle>7.5. Legítimo Interesse (Art. 7º, IX, LGPD)</Subtitle>
            <Typography paragraph>
              O tratamento atende aos interesses legítimos da ADS MANAGER ou de
              terceiros, desde que não prevaleçam direitos e liberdades
              fundamentais do Titular:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Segurança da Plataforma e prevenção de fraudes, Melhoria contínua dos Serviços, Proteção ao crédito, Análise de métricas e desempenho, Divulgação institucional e cases de sucesso, preservando dados pessoais ou com anonimização, com opt-out a pedido do titular/cliente."
                />
              </ListItem>
            </List>
          </Section>

          <Section
            id="compartilhamento"
            title="8. COMPARTILHAMENTO DE DADOS PESSOAIS"
          >
            <Subtitle>8.1. Hipóteses de Compartilhamento</Subtitle>
            <Typography paragraph>
              A ADS MANAGER poderá compartilhar os dados pessoais dos Titulares
              com terceiros nas seguintes hipóteses, sempre respeitando os
              princípios da LGPD:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              8.1.1. Prestadores de Serviços (Operadores)
            </Typography>
            <Typography paragraph>
              Os dados pessoais poderão ser compartilhados com prestadores de
              serviços que atuam como Operadores em nome da ADS MANAGER,
              mediante a celebração de acordos de tratamento de dados que
              assegurem a proteção adequada:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Stripe: Processamento de pagamentos com cartão de crédito. Os dados financeiros são tratados diretamente pela plataforma Stripe, que possui suas próprias políticas de privacidade e certificações de segurança internacionais (PCI-DSS)."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Google: Gerenciamento e otimização de perfis comerciais. O acesso aos perfis no Google é realizado mediante autenticação do Cliente, sendo necessário para a prestação dos Serviços contratados."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Agentes Comerciais Credenciados: Intermediação do processo de captação, adesão e implementação das primeiras configurações dos Serviços. Os agentes comerciais têm acesso limitado aos dados necessários para o atendimento inicial e cobrança da taxa de adesão."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Provedores de infraestrutura tecnológica: Serviços de hospedagem, armazenamento em nuvem, gerenciamento de banco de dados e outras soluções tecnológicas necessárias à operação da Plataforma."
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              8.1.2. Autoridades Públicas e Cumprimento Legal
            </Typography>
            <Typography paragraph>
              Os dados pessoais poderão ser compartilhados com autoridades
              judiciais, administrativas ou governamentais competentes, sempre
              que houver determinação legal, requisição, intimação ou ordem
              judicial.
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              8.1.3. Operações Societárias
            </Typography>
            <Typography paragraph>
              Em caso de operações de fusão, aquisição, reorganização societária
              ou venda de ativos, os dados pessoais poderão ser transferidos,
              assegurado que a parte receptora tenha o mesmo nível de proteção
              estabelecido nesta Política.
            </Typography>
            <Subtitle>8.2. Vedações ao Compartilhamento</Subtitle>
            <Typography paragraph>
              A ADS MANAGER não compartilha, vende, aluga ou comercializa dados
              pessoais dos Clientes com terceiros para fins de marketing,
              publicidade ou quaisquer outras finalidades não relacionadas à
              prestação dos Serviços, salvo mediante consentimento específico do
              Titular.
            </Typography>
            <Subtitle>8.3. Transferência Internacional de Dados</Subtitle>
            <Typography paragraph>
              Considerando que alguns dos prestadores de serviços utilizados
              pela ADS MANAGER, Stripe (pagamentos – PCI-DSS) e Google
              (infraestrutura/API). As transferências observarão o art. 33 da
              LGPD (cláusulas contratuais padrão, garantias adequadas e/ou
              consentimento, quando aplicável). Podem manter infraestrutura em
              outros países, é possível que ocorra transferência internacional
              de dados pessoais.
            </Typography>
            <Typography paragraph>
              Nessas hipóteses, a ADS MANAGER assegura que a transferência
              internacional observa as garantias previstas no artigo 33 da LGPD,
              incluindo:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Adequação do país de destino aos padrões de proteção de dados, Cláusulas contratuais específicas de proteção, Certificações e selos reconhecidos internacionalmente, Consentimento específico do Titular, quando aplicável"
                />
              </ListItem>
            </List>
            <Subtitle>8.4. Direito de Informação</Subtitle>
            <Typography paragraph>
              O Titular tem o direito de solicitar informações sobre as
              entidades públicas e privadas com as quais a ADS MANAGER realizou
              compartilhamento de dados, nos termos do artigo 18, VII, da LGPD.
            </Typography>
          </Section>

          <Section
            id="armazenamento"
            title="9. ARMAZENAMENTO E RETENÇÃO DE DADOS"
          >
            <Subtitle>9.1. Período de Armazenamento</Subtitle>
            <Typography paragraph>
              Os dados pessoais serão armazenados pela ADS MANAGER apenas pelo
              período necessário para o cumprimento das finalidades para as
              quais foram coletados, respeitando os seguintes critérios:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Durante a vigência contratual: Todos os dados necessários para a prestação dos Serviços serão mantidos enquanto o contrato estiver ativo."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Após o término do contrato: Os dados poderão ser mantidos pelo prazo adicional necessário para: Cumprimento de obrigações legais (ex.: prazo de guarda de documentos fiscais), Exercício regular de direitos (ex.: defesa em eventuais processos judiciais), Atendimento a determinações de autoridades competentes, Outros prazos legais aplicáveis, Finalidade de winback (trazer de volta para a base)."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Dados de pagamento: Os dados de cartão de crédito processados pela Stripe são armazenados exclusivamente por essa plataforma, que segue padrões internacionais de segurança (PCI-DSS)."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Registros de aceite (IP, data/hora, versão do termo/política) e dos atendimentos aos direitos (art. 18 da LGPD) serão mantidos por 5 (cinco) anos após o término contratual."
                />
              </ListItem>
            </List>
            <Subtitle>9.2. Exclusão de Dados</Subtitle>
            <Typography paragraph>
              Ao término do prazo de retenção, ou mediante solicitação do
              Titular (quando aplicável), os dados pessoais serão: Eliminados de
              forma segura e definitiva; ou Anonimizados, tornando impossível a
              identificação do Titular.
            </Typography>
            <Subtitle>9.3. Armazenamento Seguro</Subtitle>
            <Typography paragraph>
              A ADS MANAGER adota medidas técnicas e organizacionais adequadas
              para garantir que os dados pessoais sejam armazenados de forma
              segura, conforme detalhado na Seção 10.
            </Typography>
          </Section>

          <Section id="seguranca" title="10. MEDIDAS DE SEGURANÇA">
            <Subtitle>10.1. Compromisso com a Segurança</Subtitle>
            <Typography paragraph>
              A ADS MANAGER adota medidas de segurança técnicas e
              administrativas aptas a proteger os dados pessoais de acessos não
              autorizados e de situações acidentais ou ilícitas de destruição,
              perda, alteração, comunicação ou qualquer forma de tratamento
              inadequado ou ilícito, em conformidade com o artigo 46 da LGPD.
            </Typography>
            <Subtitle>10.2. Medidas Técnicas Implementadas</Subtitle>
            <Typography paragraph>
              A ADS MANAGER implementa as seguintes medidas técnicas de
              segurança:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              10.2.1. Controle de Acesso
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Autenticação obrigatória para acesso à Plataforma, Implementação de políticas de senhas fortes, Autenticação multifator quando aplicável, Segregação de perfis e permissões de acesso, Controle de sessões e logout automático"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.2.2. Criptografia
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Criptografia de dados em trânsito (protocolo HTTPS/TLS), Criptografia de dados sensíveis em repouso, Proteção de credenciais de acesso, Tokenização de dados de pagamento"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.2.3. Proteção de Infraestrutura
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Firewalls e sistemas de detecção de intrusão, Antivírus e antimalware atualizados, Monitoramento contínuo de ameaças, Proteção contra ataques DDoS, Segmentação de rede"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.2.4. Backup e Recuperação
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Realização periódica de backups, Armazenamento seguro de cópias de segurança, Plano de recuperação de desastres, Testes regulares de restauração"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.2.5. Auditoria e Monitoramento
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Registro de logs de acesso e atividades, Monitoramento de eventos de segurança, Análise de comportamentos anômalos, Auditorias periódicas de segurança"
                />
              </ListItem>
            </List>
            <Subtitle>10.3. Medidas Organizacionais Implementadas</Subtitle>
            <Typography paragraph>
              Além das medidas técnicas, a ADS MANAGER adota as seguintes
              medidas organizacionais:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              10.3.1. Políticas e Procedimentos
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Política de Segurança da Informação, Procedimentos operacionais padronizados, Política de classificação de dados, Procedimentos de resposta a incidentes"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.3.2. Gestão de Pessoal
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Termo de confidencialidade assinado por colaboradores, Treinamento periódico sobre proteção de dados, Conscientização sobre práticas de segurança, Definição clara de responsabilidades"
                />
              </ListItem>
            </List>
            <Typography variant="subtitle1" gutterBottom>
              10.3.3. Gestão de Fornecedores
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Cláusulas de proteção de dados em contratos, Avaliação de segurança de fornecedores, Monitoramento de prestadores de serviços, Acordos de nível de serviço (SLA)"
                />
              </ListItem>
            </List>
            <Subtitle>10.4. Limitações de Responsabilidade</Subtitle>
            <Typography paragraph>
              Não obstante as medidas de segurança implementadas, a ADS MANAGER
              não pode garantir a segurança absoluta dos dados pessoais,
              considerando que nenhum sistema é completamente imune a ataques ou
              violações.
            </Typography>
            <Typography paragraph>
              A ADS MANAGER não será responsável por:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="- Danos decorrentes de caso fortuito ou força maior, Atos de terceiros não autorizados que violem as medidas de segurança, Uso indevido de credenciais pelo próprio Titular, Vulnerabilidades em sistemas de terceiros (ex.: navegadores, sistemas operacionais)"
                />
              </ListItem>
            </List>
            <Subtitle>10.5. Resposta a Incidentes de Segurança</Subtitle>
            <Typography paragraph>
              Em caso de incidente de segurança que possa acarretar risco ou
              dano relevante aos Titulares, a ADS MANAGER se compromete a:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="1. Comunicar à ANPD e aos titulares afetados em até 5 (dias) dias úteis da detecção, ou no prazo que vier a ser regulado;"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="2. Notificar o Titular afetado, informando sobre: A natureza e extensão do incidente, Os dados pessoais afetados, As medidas técnicas e de segurança adotadas, Os riscos relacionados ao incidente, As medidas que o Titular pode adotar para mitigar os efeitos"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="3. Adotar medidas corretivas imediatas para conter e remediar o incidente;"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="4. Documentar o incidente e as ações tomadas."
                />
              </ListItem>
            </List>
          </Section>

          <Section id="cookies" title="11. COOKIES E TECNOLOGIAS SIMILARES">
            <Subtitle>11.1. O que são Cookies</Subtitle>
            <Typography paragraph>
              Cookies são pequenos arquivos de texto armazenados no dispositivo
              do usuário quando este acessa um site ou aplicação. Eles permitem
              que a Plataforma reconheça o dispositivo e armazene informações
              sobre preferências e ações do usuário.
            </Typography>
            <Subtitle>11.2. Tipos de Cookies Utilizados</Subtitle>
            <Typography paragraph>
              A ADS MANAGER utiliza as seguintes categorias de cookies em sua
              Plataforma:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.2.1. Cookies Essenciais (Necessários)
            </Typography>
            <Typography paragraph>
              São cookies indispensáveis para o funcionamento básico da
              Plataforma, permitindo a navegação e utilização de funcionalidades
              essenciais, como: Autenticação e manutenção de sessão, Segurança
              da plataforma, Lembrança de preferências de configuração,
              Balanceamento de carga.
            </Typography>
            <Typography paragraph>
              Base legal: Legítimo interesse (Art. 7º, IX, LGPD)
              <br />
              Período de retenção: Sessão ou conforme necessário para a
              funcionalidade
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.2.2. Cookies de Desempenho e Analíticos
            </Typography>
            <Typography paragraph>
              Coletam informações sobre como os usuários utilizam a Plataforma,
              permitindo melhorias na experiência: Páginas mais visitadas, Tempo
              de permanência, Caminhos de navegação, Identificação de erros.
            </Typography>
            <Typography paragraph>
              Base legal: Consentimento (Art. 7º, I, LGPD)
              <br />
              Período de retenção: Até 24 meses
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.2.3. Cookies de Funcionalidade
            </Typography>
            <Typography paragraph>
              Permitem que a Plataforma se lembre de escolhas do usuário e
              ofereça recursos aprimorados: Idioma preferido, Preferências de
              visualização, Personalização de interface.
            </Typography>
            <Typography paragraph>
              Base legal: Consentimento (Art. 7º, I, LGPD)
              <br />
              Período de retenção: Até 12 meses
            </Typography>
            <Subtitle>11.3. Cookies de Terceiros</Subtitle>
            <Typography paragraph>
              A ADS MANAGER pode utilizar cookies de terceiros para finalidades
              específicas:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Google Analytics: Análise de tráfego e comportamento na Plataforma"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Stripe: Processamento seguro de pagamentos"
                />
              </ListItem>
            </List>
            <Typography paragraph>
              Esses cookies são regidos pelas políticas de privacidade dos
              respectivos terceiros.
            </Typography>
            <Subtitle>11.4. Gestão de Cookies</Subtitle>
            <Typography paragraph>
              O usuário pode gerenciar suas preferências de cookies das
              seguintes formas:
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.4.1. Banner de Cookies
            </Typography>
            <Typography paragraph>
              Ao acessar a Plataforma pela primeira vez, será exibido um banner
              informando sobre o uso de cookies, permitindo que você: Aceite
              todos os cookies, Rejeite cookies não essenciais, Personalize suas
              preferências por categoria.
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.4.2. Configurações do Navegador
            </Typography>
            <Typography paragraph>
              Você pode configurar seu navegador para: Bloquear todos os
              cookies, Aceitar apenas cookies de sites específicos, Receber
              notificação antes de aceitar cookies, Excluir cookies existentes.
            </Typography>
            <Typography paragraph>
              Importante: O bloqueio de cookies essenciais pode afetar o
              funcionamento da Plataforma.
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              11.4.3. Ferramentas de Opt-out
            </Typography>
            <Typography paragraph>
              Você pode utilizar ferramentas e extensões de navegador que
              permitem controlar o rastreamento, tais como: Privacy Badger,
              Ghostery, uBlock Origin, Do Not Track (DNT).
            </Typography>
            <Subtitle>11.5. Outras Tecnologias de Rastreamento</Subtitle>
            <Typography paragraph>
              Além de cookies, a ADS MANAGER pode utilizar tecnologias
              similares como: Web beacons (pixels), Local Storage, Session
              Storage. Essas tecnologias são utilizadas para as mesmas
              finalidades dos cookies e estão sujeitas às mesmas práticas de
              privacidade.
            </Typography>
          </Section>

          <Section id="direitos" title="12. DIREITOS DOS TITULARES">
            <Subtitle>12.1. Direitos Garantidos pela LGPD</Subtitle>
            <Typography paragraph>
              Em conformidade com o artigo 18 da LGPD, todo titular de dados
              pessoais possui os seguintes direitos em relação aos seus dados:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Confirmação e Acesso (Art. 18, I e II): Direito de obter confirmação sobre a existência de tratamento de dados pessoais e acesso aos dados tratados pela ADS MANAGER."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Correção (Art. 18, III): Direito de solicitar a correção de dados incompletos, inexatos ou desatualizados."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Anonimização, Bloqueio ou Eliminação (Art. 18, IV): Direito de solicitar a anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a LGPD."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Portabilidade (Art. 18, V): Direito de solicitar a portabilidade dos dados a outro fornecedor de serviço ou produto, mediante requisição expressa e de acordo com a regulamentação da ANPD."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Eliminação (Art. 18, VI): Direito de solicitar a eliminação dos dados pessoais tratados com base no consentimento, exceto nas hipóteses de conservação previstas no artigo 16 da LGPD."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Informação sobre Compartilhamento (Art. 18, VII): Direito de obter informações sobre as entidades públicas e privadas com as quais a ADS MANAGER realizou compartilhamento de dados."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Informação sobre Não Consentimento (Art. 18, VIII): Direito de ser informado sobre a possibilidade de não fornecer consentimento e sobre as consequências da negativa."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary="Revogação do Consentimento (Art. 18, IX): Direito de revogar o consentimento a qualquer momento, mediante manifestação expressa, por procedimento gratuito e facilitado."
                />
              </ListItem>
            </List>
            <Subtitle>12.2. Exercício dos Direitos</Subtitle>
            <Typography paragraph>
              Para exercer qualquer um dos direitos acima, o Titular deve entrar
              em contato com a ADS MANAGER através dos seguintes canais:
            </Typography>
            <Typography paragraph>
              Exercício de Direitos: “Para opt-out de cases/marketing, envie
              solicitação ao DPO; prazo de atendimento: 15 dias.”
            </Typography>
            <Typography paragraph>
              E-mail do DPO: [privacidade@ads.software]
              <br />
              Formulário online: adssoftware.com.br/privacidade/solicitacoes
            </Typography>
            <Subtitle>12.3. Prazo de Resposta</Subtitle>
            <Typography paragraph>
              A ADS MANAGER se compromete a responder às solicitações dos
              Titulares nos seguintes prazos: Formato simplificado:
              Imediatamente, sempre que possível; Declaração completa: Até 15
              (quinze) dias, contados da data do requerimento.
            </Typography>
            <Subtitle>12.4. Gratuidade</Subtitle>
            <Typography paragraph>
              O exercício dos direitos dos titulares é gratuito. A ADS MANAGER
              poderá cobrar valores apenas em casos de requisições
              manifestamente infundadas ou excessivas, com base em custos
              administrativos.
            </Typography>
            <Subtitle>12.5. Limitações ao Exercício dos Direitos</Subtitle>
            <Typography paragraph>
              O exercício de determinados direitos pode ser limitado ou recusado
              nas seguintes hipóteses: Cumprimento de obrigação legal ou
              regulatória, Estudo por órgão de pesquisa (com dados
              anonimizados), Execução de contrato, Exercício regular de direitos
              em processo judicial, administrativo ou arbitral, Proteção da vida
              ou incolumidade física, Tutela da saúde, Quando o tratamento for
              indispensável para atender ao interesse legítimo do controlador.
            </Typography>
            <Typography paragraph>
              Nesses casos, a ADS MANAGER informará ao Titular os motivos da
              limitação.
            </Typography>
          </Section>

          <Section id="menores" title="13. DADOS DE MENORES DE IDADE">
            <Subtitle>13.1. Restrição de Uso</Subtitle>
            <Typography paragraph>
              Os Serviços da ADS MANAGER são destinados exclusivamente a
              pessoas jurídicas (empresas) e seus representantes legais maiores
              de 18 (dezoito) anos. A Plataforma não é direcionada a menores de
              idade e a ADS MANAGER não coleta intencionalmente dados pessoais
              de crianças ou adolescentes.
            </Typography>
            <Subtitle>13.2. Tratamento Acidental</Subtitle>
            <Typography paragraph>
              Caso a ADS MANAGER identifique que coletou inadvertidamente dados
              pessoais de menores de 18 anos sem o devido consentimento dos pais
              ou responsáveis legais, tais dados serão eliminados o mais
              rapidamente possível.
            </Typography>
            <Subtitle>13.3. Comunicação de Irregularidades</Subtitle>
            <Typography paragraph>
              Caso tome conhecimento de que dados de menores foram fornecidos à
              Plataforma, solicitamos que entre em contato imediatamente com o
              DPO através dos canais indicados nesta Política.
            </Typography>
          </Section>

          <Section
            id="alteracoes"
            title="14. ALTERAÇÕES NA POLÍTICA DE PRIVACIDADE"
          >
            <Subtitle>14.1. Direito de Atualização</Subtitle>
            <Typography paragraph>
              A ADS MANAGER reserva-se o direito de modificar, a qualquer
              tempo, esta Política de Privacidade, visando seu aprimoramento e
              adequação a eventuais alterações legislativas, regulamentares ou
              jurisprudenciais, bem como a mudanças nas práticas de tratamento
              de dados.
            </Typography>
            <Subtitle>14.2. Comunicação de Alterações</Subtitle>
            <Typography paragraph>
              Sempre que houver alterações relevantes nesta Política, a ADS
              MANAGER: Atualizará a data de "última atualização" no início
              deste documento, Comunicará os Clientes através de e-mail
              cadastrado ou notificação na Plataforma, Disponibilizará a nova
              versão de forma destacada na Plataforma.
            </Typography>
            <Subtitle>14.3. Aceitação das Alterações</Subtitle>
            <Typography paragraph>
              A continuidade do uso da Plataforma após a comunicação das
              alterações implicará na aceitação da nova Política de Privacidade.
              Caso não concorde com as alterações, o Cliente poderá solicitar o
              cancelamento dos Serviços conforme previsto no Termo de Uso.
            </Typography>
            <Subtitle>14.4. Histórico de Versões</Subtitle>
            <Typography paragraph>
              Versões anteriores desta Política de Privacidade ficarão
              disponíveis para consulta mediante solicitação ao DPO.
            </Typography>
          </Section>

          <Section id="legislacao" title="15. LEGISLAÇÃO E FORO APLICÁVEIS">
            <Subtitle>15.1. Lei Aplicável</Subtitle>
            <Typography paragraph>
              Esta Política de Privacidade é regida e interpretada de acordo com
              as leis da República Federativa do Brasil, especialmente a Lei nº
              13.709/2018 (LGPD), a Lei nº 12.965/2014 (Marco Civil da Internet)
              e demais normas aplicáveis.
            </Typography>
            <Subtitle>15.2. Foro de Eleição</Subtitle>
            <Typography paragraph>
              As partes elegem o foro da Comarca de Goiânia, Estado de Goiás,
              para dirimir quaisquer controvérsias oriundas desta Política de
              Privacidade ou relacionadas ao tratamento de dados pessoais,
              renunciando expressamente a qualquer outro, por mais privilegiado
              que seja.
            </Typography>
            <Typography paragraph>
              Esta eleição de foro não se aplica aos Titulares que sejam
              consumidores pessoas físicas, os quais poderão optar pelo foro de
              seu domicílio, conforme o artigo 101, I, do Código de Defesa do
              Consumidor.
            </Typography>
          </Section>

          <Section id="disposicoes-finais" title="16. DISPOSIÇÕES FINAIS">
            <Subtitle>16.1. Independência das Cláusulas</Subtitle>
            <Typography paragraph>
              Caso qualquer disposição desta Política de Privacidade seja
              considerada inválida, ilegal ou inexequível, tal invalidade não
              afetará as demais disposições, que permanecerão em pleno vigor e
              efeito.
            </Typography>
            <Subtitle>16.2. Relação com Outros Documentos</Subtitle>
            <Typography paragraph>
              Esta Política de Privacidade complementa e deve ser lida em
              conjunto com o Termo de Uso da Plataforma ADS MANAGER. Em caso de
              conflito, prevalecerá a disposição mais protetiva ao titular, sem
              prejuízo das condições comerciais previstas no Termo.
            </Typography>
            <Subtitle>16.3. Idioma</Subtitle>
            <Typography paragraph>
              Esta Política de Privacidade é redigida em língua portuguesa,
              sendo esta a versão oficial e prevalente em caso de eventuais
              traduções.
            </Typography>
            <Subtitle>16.4. Dúvidas e Informações Adicionais</Subtitle>
            <Typography paragraph>
              Para esclarecer dúvidas sobre esta Política de Privacidade ou
              sobre o tratamento de dados pessoais pela ADS MANAGER, entre em
              contato com nosso Encarregado de Proteção de Dados (DPO):
            </Typography>
            <Typography paragraph>
              <strong>Encarregado de Proteção de Dados (DPO)</strong>
              <br />
              <strong>Nome:</strong> Walesca Cardoso do Nascimento
              <br />
              <strong>E-mail:</strong> privacidade@adssoftware.com.br
              <br />
              <strong>Endereço:</strong> : Avenida Monte Carlo, 231, Goiânia/GO,
              CEP 74370-40
              <br />
              <strong>Telefone:</strong> 0800 800 1700
            </Typography>
            <Subtitle>16.5. Canal com a ANPD</Subtitle>
            <Typography paragraph>
              O Titular também poderá registrar reclamações ou denúncias
              diretamente junto à Autoridade Nacional de Proteção de Dados
              (ANPD):
              <br />
              https://www.gov.br/anpd
              <br />
              Endereço: SIA Trecho 01 Lote 1565, Zona Industrial, Guará/DF, CEP
              71.200-001
            </Typography>
            <Typography paragraph sx={{ mt: 4 }}>
              <strong>ADS MANAGER</strong> — CNPJ 63.460.077/0001-64
              <br />
              Terra Office Av. C-4, 931 - Jardim América, Goiânia - GO, CEP
              74265-040 — sac@georanking.com.br
            </Typography>
            <Typography paragraph>
              Esta Política de Privacidade foi elaborada em conformidade com a
              Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018) e
              demais normas aplicáveis à proteção de dados e privacidade.
            </Typography>
          </Section>
        </Container>
      </Box>
    </Box>
  );
};
